import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { endSalesReporterSession } from "@/lib/salesReporterSso";

// Distinguishes a real server response (with a `detail`) from a request that
// never got a response at all (network failure, CORS block, backend down).
// Without this, e.response being undefined made every such failure surface
// as the unhelpful generic "Something went wrong." regardless of the real
// cause, which is confusing when it happens even with valid credentials.
function formatRequestError(e) {
  if (e.response) return formatApiError(e.response.data?.detail) || e.message;
  return e.message || "Could not reach the server. Please check your connection and try again.";
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = not-auth
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (e) {
      localStorage.removeItem("token");
      setUser(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("token", data.token);
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatRequestError(e));
      return false;
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.token) localStorage.setItem("token", data.token);
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatRequestError(e));
      return false;
    }
  };

  const logout = async () => {
    // End the Sales Reporter session first — that call still needs the ERP sign-in.
    await endSalesReporterSession();
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, error, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
